import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

/**
 * Counts up to the numeric part of `value` when scrolled into view,
 * then settles on the original value. Preserves any prefix/suffix
 * (e.g. "500+", "98%", "24/7").
 */
const AnimatedCounter = ({ value, duration = 1800, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(() => {
    const m = String(value).match(/\d+/);
    return m ? String(value).replace(m[0], '0') : String(value);
  });

  useEffect(() => {
    if (!inView) return;
    const str = String(value);
    const numMatch = str.match(/\d+/);
    if (!numMatch) {
      setDisplay(str);
      return;
    }
    const target = parseInt(numMatch[0], 10);
    const prefix = str.slice(0, numMatch.index);
    const suffix = str.slice(numMatch.index + numMatch[0].length);

    let startTime;
    let raf;
    const step = (t) => {
      if (!startTime) startTime = t;
      const progress = Math.min((t - startTime) / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const current = Math.round(eased * target);
      setDisplay(`${prefix}${current}${suffix}`);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default AnimatedCounter;
